/**
 * Brand Kit (spec §22-24). Tenant-level, with optional auction overrides,
 * resolved along the same hierarchy the rest of the platform uses:
 *
 *   HipNumber platform defaults -> Tenant Brand Kit -> Auction Brand override
 *
 * The override replaces a subtree WHOLESALE, never key-by-key - the same rule
 * as the money fields and ADR-55 branding (the ADR-33 rule). This package
 * declares the shape and the platform defaults; `@hipnumber/db` owns storage
 * and the effective resolver, exactly as `effectiveBranding()` is the one
 * resolver for branding.
 *
 * The snapshot is the point of the file. A published campaign must stay
 * reproducible after the tenant changes its logo next month (spec §24), so at
 * generation time the EFFECTIVE brand is frozen into a BrandKitSnapshot and the
 * campaign depends on the snapshot, never on live brand state.
 */

import { z } from 'zod';
import { HexColorSchema } from './ids.js';

// ---------------------------------------------------------------------------
// Logos, colours, type
// ---------------------------------------------------------------------------

/** Logos are references into the Media Library, resolved to rendered image
 *  objects only at snapshot time. A Brand Kit never holds a raw URL. */
export const BrandLogosSchema = z
  .object({
    primaryAssetId: z.string().min(1).optional(),
    secondaryAssetId: z.string().min(1).optional(),
    iconAssetId: z.string().min(1).optional(),
    lightAssetId: z.string().min(1).optional(),
    darkAssetId: z.string().min(1).optional(),
  })
  .strict();
export type BrandLogos = z.infer<typeof BrandLogosSchema>;

export const BrandColorsSchema = z
  .object({
    primary: HexColorSchema,
    secondary: HexColorSchema.optional(),
    accent: HexColorSchema.optional(),
    background: HexColorSchema.optional(),
    surface: HexColorSchema.optional(),
    text: HexColorSchema.optional(),
    muted: HexColorSchema.optional(),
  })
  .strict();
export type BrandColors = z.infer<typeof BrandColorsSchema>;

export const BrandTypographySchema = z
  .object({
    headingFont: z.string().optional(),
    bodyFont: z.string().optional(),
    headingWeight: z.number().int().min(100).max(900).optional(),
    bodyWeight: z.number().int().min(100).max(900).optional(),
  })
  .strict();
export type BrandTypography = z.infer<typeof BrandTypographySchema>;

export const BrandCreativeDirectionSchema = z
  .object({
    defaultStyle: z.string().optional(),
    tone: z.array(z.string()).optional(),
    /** Nudges the prompt planner toward these. */
    preferredKeywords: z.array(z.string()).optional(),
    /** Hard negatives the planner must pass to the provider (spec §42). */
    blockedKeywords: z.array(z.string()).optional(),
  })
  .strict();
export type BrandCreativeDirection = z.infer<typeof BrandCreativeDirectionSchema>;

export const BrandDefaultsSchema = z
  .object({
    cta: z.string().optional(),
    disclaimer: z.string().optional(),
  })
  .strict();
export type BrandDefaults = z.infer<typeof BrandDefaultsSchema>;

// ---------------------------------------------------------------------------
// Brand Kit (mutable, tenant-owned)
// ---------------------------------------------------------------------------

export const BrandKitSchema = z
  .object({
    id: z.string().min(1),
    tenantId: z.string().min(1),
    name: z.string().min(1),
    logos: BrandLogosSchema,
    colors: BrandColorsSchema,
    typography: BrandTypographySchema.optional(),
    creativeDirection: BrandCreativeDirectionSchema.optional(),
    defaults: BrandDefaultsSchema.optional(),
  })
  .strict();
export type BrandKit = z.infer<typeof BrandKitSchema>;

/** The subset an auction may override, replacing subtrees wholesale. */
export const AuctionBrandOverrideSchema = z
  .object({
    logos: BrandLogosSchema.optional(),
    colors: BrandColorsSchema.optional(),
    tagline: z.string().max(200).optional(),
    heroAssetId: z.string().min(1).optional(),
    creativeDirection: BrandCreativeDirectionSchema.optional(),
  })
  .strict();
export type AuctionBrandOverride = z.infer<typeof AuctionBrandOverrideSchema>;

// ---------------------------------------------------------------------------
// Brand Kit snapshot (immutable, campaign-owned)
// ---------------------------------------------------------------------------

/** A logo frozen into a snapshot: the resolved rendition, not a Media id, so
 *  the snapshot stays valid even if the library asset is later replaced. */
/** Where the subject is, as fractions of the image. Frozen with the rendition
 *  so every format's cover crop is derived from the same point (ADR-56
 *  hardening B); absent means centre. */
export const FocalPointSchema = z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).strict();
export type FocalPoint = z.infer<typeof FocalPointSchema>;

export const SnapshotImageSchema = z
  .object({ url: z.string().min(1), width: z.number().int().positive(), height: z.number().int().positive(), focal: FocalPointSchema.optional() })
  .strict();
export type SnapshotImage = z.infer<typeof SnapshotImageSchema>;

export const BrandKitSnapshotSchema = z
  .object({
    id: z.string().min(1),
    tenantId: z.string().min(1),
    /** The Brand Kit this was taken from, for provenance. */
    brandKitId: z.string().min(1),
    logos: z
      .object({
        primary: SnapshotImageSchema.optional(),
        secondary: SnapshotImageSchema.optional(),
        icon: SnapshotImageSchema.optional(),
        light: SnapshotImageSchema.optional(),
        dark: SnapshotImageSchema.optional(),
      })
      .strict(),
    colors: BrandColorsSchema,
    typography: BrandTypographySchema.optional(),
    creativeDirection: BrandCreativeDirectionSchema.optional(),
    defaults: BrandDefaultsSchema.optional(),
    /** A real photograph of the venue / the sale (the ADR-55 branded hero),
     *  frozen as a rendition so a REAL_MEDIA creative stays reproducible after
     *  the tenant swaps the photo (ADR-56 cut 8). */
    hero: SnapshotImageSchema.optional(),
    /** When this snapshot was frozen. */
    generatedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type BrandKitSnapshot = z.infer<typeof BrandKitSnapshotSchema>;

// ---------------------------------------------------------------------------
// Platform defaults (top of the hierarchy)
// ---------------------------------------------------------------------------

/**
 * HipNumber's own palette (spec §4). These sit at the top of the Brand Kit
 * hierarchy so a tenant with no Brand Kit still renders a coherent, on-brand
 * campaign. This is data only; any SURFACE that paints with these must go
 * through the branding skill and compute a readable ink from luminance the way
 * ADR-55 does - white-on-orange fails WCAG AA and is never hard-coded as a
 * pair here.
 */
export const PLATFORM_BRAND_COLORS = {
  primaryOrange: '#F97316',
  deepNavy: '#0F2347',
  softOffWhite: '#F7F5F2',
  coolGray: '#D9DEE7',
  charcoal: '#1F2937',
  successGreen: '#16A34A',
} as const;

export const PLATFORM_BRAND_DEFAULTS: BrandColors = {
  primary: PLATFORM_BRAND_COLORS.primaryOrange,
  secondary: PLATFORM_BRAND_COLORS.deepNavy,
  accent: PLATFORM_BRAND_COLORS.primaryOrange,
  background: PLATFORM_BRAND_COLORS.softOffWhite,
  surface: '#FFFFFF',
  text: PLATFORM_BRAND_COLORS.deepNavy,
  muted: PLATFORM_BRAND_COLORS.coolGray,
};
